import type { Service } from "../../api/types";
import { MaskedField } from "../../components/MaskedField";
import "./services.css";

/**
 * The faithful (stateful) capture fields this section renders — the structural subset
 * shared by a persisted `Service` and a pre-persist `AdoptPreview` (issue #331), so the
 * same masked-review component renders both the preview (before adopt) and the stored
 * service (after). Both carry the SAME asymmetric §4 sensitivity.
 */
export type CapturedConfig = Pick<
  Service,
  "env" | "volumes" | "command" | "entrypoint" | "restartPolicy" | "healthcheck"
>;

interface CapturedConfigurationSectionProps {
  service: CapturedConfig;
}

/**
 * Renders a service's faithful (stateful) capture fields (ADR-0038 P1/P4b) — populated
 * end to end by `adoptService` today. Renders nothing when none of these fields are set,
 * so a plain stateless service's page looks exactly as it did before this feature.
 *
 * §4 SENSITIVE-FIELD HANDLING (the hard requirement this section exists to satisfy):
 * `command`, `entrypoint`, and `healthcheck.test` were captured VERBATIM from a running
 * container and commonly embed credentials — each renders through `MaskedField`, masked
 * by default with an explicit per-field reveal, never in a title/tooltip, never logged.
 * `volumes`/`restartPolicy` are NOT classified sensitive (ADR-0038 §4 scopes sensitivity
 * to command/entrypoint/healthcheck only) and render plainly. Env is keys-only by
 * construction (the server never captures a value) — shown with an explicit "not
 * captured" note per field rather than a blank that could be mistaken for a real empty
 * value.
 */
export function CapturedConfigurationSection({ service }: CapturedConfigurationSectionProps) {
  const envKeys = Object.keys(service.env ?? {});
  const hasVolumes = Boolean(service.volumes && service.volumes.length > 0);
  const hasRestartPolicy = Boolean(service.restartPolicy?.name);
  const hasCommand = Boolean(service.command && service.command.length > 0);
  const hasEntrypoint = Boolean(service.entrypoint && service.entrypoint.length > 0);
  const hasHealthcheck = Boolean(service.healthcheck?.test && service.healthcheck.test.length > 0);
  const hasAnything = hasVolumes || hasRestartPolicy || hasCommand || hasEntrypoint || hasHealthcheck || envKeys.length > 0;

  if (!hasAnything) return null;

  return (
    <section className="service-detail__section" aria-labelledby="service-captured-heading">
      <h2 id="service-captured-heading">Captured configuration</h2>
      <p className="captured-config__note">
        Command, entrypoint, and healthcheck below were captured verbatim from the running container — they may
        contain secrets (e.g. a <code>--password</code> flag or a bearer token in a healthcheck probe), so each is
        masked by default. Use Reveal to view one at a time.
      </p>

      <MaskedField label="Command" values={service.command} emptyLabel="No command captured" />
      <MaskedField label="Entrypoint" values={service.entrypoint} emptyLabel="No entrypoint captured" />
      <MaskedField label="Healthcheck test" values={service.healthcheck?.test} emptyLabel="No healthcheck captured" />

      {envKeys.length > 0 && (
        <div className="captured-config__env">
          <h3>Environment</h3>
          <p className="captured-config__note">
            Only variable NAMES were captured — values are never read from the container or stored (the zero-egress
            firewall). Supply real values before redeploying.
          </p>
          <ul className="captured-config__env-list">
            {envKeys.map((key) => (
              <li key={key}>
                <code>{key}</code> <span className="captured-config__env-blank">— not captured</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasVolumes && (
        <div className="captured-config__volumes">
          <h3>Volumes</h3>
          <ul className="captured-config__volumes-list">
            {(service.volumes ?? []).map((v, i) => (
              <li key={`${v.target}-${i}`}>
                <code>{v.source}</code> → <code>{v.target}</code> ({v.type}
                {v.mode ? `, ${v.mode}` : ""})
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasRestartPolicy && (
        <p className="captured-config__restart">
          Restart policy: <code>{service.restartPolicy?.name}</code>
        </p>
      )}
    </section>
  );
}
